import * as React from 'react';
import { ComboBox, Item } from '@adobe/react-spectrum';
import { SortingTable, ISortingRow } from '../SortingTable';
import { IColumnSection, IColumnItem } from '../ColumnsList';
import styles from './SortingSetupPlaceholder.module.css';

export interface ISortingSetupPlaceholderProps {
  selectedColumns: IColumnSection[];
  sortingData: ISortingRow[];
  onSortingChange: (data: ISortingRow[]) => void;
}

// Helper function to flatten sections to items
const flattenSectionsToItems = (sections: IColumnSection[]): IColumnItem[] => {
  return sections.flatMap(section => section.items || []);
};

export function SortingSetupPlaceholder(props: ISortingSetupPlaceholderProps) {
  const { selectedColumns, sortingData, onSortingChange } = props;
  const [selectedColumnItemname, setSelectedColumnItemname] = React.useState<string | null>(null);

  // Flatten selected columns to items
  const availableItems = React.useMemo(() => {
    return flattenSectionsToItems(selectedColumns);
  }, [selectedColumns]);

  // Get columns already in sorting table by itemname
  const existingColumnItemnames = React.useMemo(() => {
    return new Set(sortingData.map(row => row.itemname));
  }, [sortingData]);

  // Filter out columns already in sorting table
  const availableForSelection = React.useMemo(() => {
    return availableItems.filter(item => !existingColumnItemnames.has(item.itemname));
  }, [availableItems, existingColumnItemnames]);

  // Handle adding a column from dropdown
  const handleAddColumn = React.useCallback((itemname: string | null) => {
    if (!itemname) return;

    const selectedItem = availableItems.find(item => item.itemname === itemname);
    if (!selectedItem) return;

    // Check if column already exists
    if (existingColumnItemnames.has(selectedItem.itemname)) return;

    const newRow: ISortingRow = {
      column: selectedItem.label,
      itemname: selectedItem.itemname,
      level: 'Ascending'
    };

    const updatedData = [...sortingData, newRow];
    onSortingChange(updatedData);
    setSelectedColumnItemname(null); // Reset dropdown
  }, [availableItems, existingColumnItemnames, sortingData, onSortingChange]);

  // Handle sorting data changes from SortingTable
  const handleSortingDataChange = React.useCallback((data: ISortingRow[]) => {
    onSortingChange(data);
  }, [onSortingChange]);

  return (
    <div className={styles.sortingSetupPlaceholder}>
      {sortingData.length > 0 && (
        <SortingTable
          data={sortingData}
          onDataChange={handleSortingDataChange}
        />
      )}
      <div className={styles.addColumnContainer}>
        <ComboBox
          selectedKey={selectedColumnItemname}
          onSelectionChange={(key) => {
            setSelectedColumnItemname(key as string);
            handleAddColumn(key as string);
          }}
          defaultItems={availableForSelection}
          isDisabled={availableForSelection.length === 0}
          aria-label="Add column to sorting"
          description="Add column"
        >
          {(item) => <Item key={item.itemname}>{item.label}</Item>}
        </ComboBox>
      </div>
    </div>
  );
}

